import React, { useEffect, useRef } from "react";
import HeadingBox from "../../components/common/HeadingBox";
import Card from "../../components/common/Card";
import Grid from "../../components/common/Grid";
import Button from "../../components/common/Button";
import { Box, Text, Flex } from "@radix-ui/themes";
import founderImage from "../../assets/home/alondra.png";
import gabyImage from "../../assets/home/gaby.png";
import marcelleImage from "../../assets/home/marcelle.png";
import irinaImage from "../../assets/home/irina.png";
import restomImage from "../../assets/home/doctor.jpg";
import faceCareImage from "../../assets/home/services/face-care.jpg";
import whyUs1 from "../../assets/home/why-us/1.jpg";
import whyUs2 from "../../assets/home/why-us/2.jpg";
import whyUs3 from "../../assets/home/why-us/3.jpg";
import whyUs4 from "../../assets/home/why-us/4.jpg";
import hairCareImage from "../../assets/home/services/hair-care.jpg";
import eyeCareImage from "../../assets/home/services/eye-care.jpg";
import laserImage from "../../assets/home/services/laser.jpg";
import physiotherapyImage from "../../assets/home/services/physiotherapy.jpg";
import styles from "./styles.module.scss";
import {
  animateTeamMembers,
  animateTrustSection,
  animateCTASection,
  refreshScrollTrigger,
} from "../../utils/animations";

const Team = () => {
  const teamMembersRef = useRef(null);
  const trustSectionRef = useRef(null);
  const ctaSectionRef = useRef(null);

  // Animation for Team Members
  useEffect(() => {
    return animateTeamMembers(teamMembersRef);
  }, []);

  // Animation for Trust Section
  useEffect(() => {
    return animateTrustSection(trustSectionRef);
  }, []);

  // Animation for CTA Section
  useEffect(() => {
    return animateCTASection(ctaSectionRef);
  }, []);

  // Refresh ScrollTrigger after all animations are set up
  useEffect(() => {
    return refreshScrollTrigger();
  }, []);

  const teamMembers = [
    {
      name: "Dr. Alondra Azar",
      role: "CEO, Founder & Skincare Specialist",
      credentials: "PhD in Pharmacy",
      specialty: "Face Care Specialist",
      image: founderImage,
      description:
        "As our founder, Dr. Alondra brings her extensive pharmaceutical expertise and passion for skincare to every treatment. She specializes in face care, combining medical knowledge with personalized solutions to help reveal your natural beauty.",
    },
    {
      name: "Dr. Gabriella Kassouf",
      role: "Physical Therapy Doctor",
      credentials: "Doctor in Physical Therapy",
      specialty: "Body Care Specialist",
      image: gabyImage,
      description:
        "With her expertise in physical therapy, Dr. Gabriella focuses on everything related to body care. She provides comprehensive treatments that promote healing, strength, and overall physical wellbeing.",
    },
    {
      name: "Marcelle Saad",
      role: "Laser & Epilation Specialist",
      credentials: "Nurse",
      specialty: "5 Years Experience",
      image: marcelleImage,
      description:
        "Marcelle brings 5 years of specialized experience in laser treatments and épilation électrique. Her expertise ensures safe and effective hair removal and laser procedures with precision and care.",
    },
    {
      name: "Dr. Restom Dirani",
      role: "Dermatologist",
      credentials: "Board Certified Dermatologist",
      specialty: "10 Years Experience",
      image: restomImage,
      description:
        "With 10 years of experience in dermatology, Dr. Restom provides expert skin care solutions. His extensive knowledge helps address various skin concerns with advanced medical treatments.",
    },
    {
      name: "Irina Azar",
      role: "Lashes Specialist",
      credentials: "Certified Lash Artist",
      specialty: "Eyelash Enhancement",
      image: irinaImage,
      description:
        "Irina specializes in creating beautiful, natural-looking lashes. Her expertise in lash treatments helps enhance your eyes and boost your confidence with precision and artistry.",
    },
  ];

  // Dummy data for Trust & Expertise section
  // Using whyUs images that aren't used elsewhere on this page
  const trustItems = [
    {
      image: whyUs1,
      text: "Licensed Medical Professionals",
    },
    {
      image: whyUs2,
      text: "Board Certified Specialists",
    },
    {
      image: whyUs3,
      text: "Years of Combined Experience",
    },
    {
      image: whyUs4,
      text: "Evidence-Based Treatments",
    },
  ];

  return (
    <div style={{ background: "var(--color-secondary)", minHeight: "100vh" }}>
      {/* Team Members Section */}
      <Box ref={teamMembersRef} className={styles.teamMembersSection}>
        <HeadingBox
          title="Meet Our Team"
          description="Medical experts committed to your transformation"
          textColor="var(--color-secondary)"
        />
        <div className={styles.teamGrid}>
          {teamMembers.map((member, index) => (
            <Box key={index} className={styles.teamCard}>
              <Box className={styles.teamImageWrapper}>
                <img
                  src={member.image}
                  alt={member.name}
                  className={styles.teamImage}
                />
              </Box>
              <Box className={styles.teamContent}>
                <Text className={styles.teamName}>{member.name}</Text>
                <Text className={styles.teamRole}>{member.role}</Text>
                <Text className={styles.teamCredentials}>
                  {member.credentials}
                </Text>
                <Text className={styles.teamSpecialty}>{member.specialty}</Text>
                <Text className={styles.teamDescription}>
                  {member.description}
                </Text>
              </Box>
            </Box>
          ))}
        </div>
      </Box>

      {/* Trust & Expertise Section */}
      <Box ref={trustSectionRef} className={styles.trustSection}>
        <Card
          image={faceCareImage}
          imagePosition="left"
          subtitle="Trust & Expertise"
          title="A Trustworthy Clinic with Medical Professionals Who Know What They're Doing"
          description="At Renova Aesthetic Clinic, every member of our team is a licensed medical professional with specialized training and years of experience. We don't compromise on safety or expertise—every treatment is performed by qualified doctors, nurses, and certified specialists who understand the medical principles behind aesthetic care. Our commitment to ongoing education and certification ensures that we stay at the forefront of medical aesthetics, always prioritizing your safety and delivering results you can trust."
          backgroundColor="var(--color-secondary)"
          cardBackgroundColor="var(--color-secondary)"
          textColor="var(--color-primary)"
          sectionPadding="4rem 2rem"
          cardPadding="3rem"
        />
        <Box className={styles.trustGridWrapper}>
          <Grid
            items={trustItems}
            columns={4}
            backgroundColor="var(--color-secondary)"
            gap="20px"
          />
        </Box>
      </Box>

      {/* Call to Action Section */}
      <Box ref={ctaSectionRef} className={styles.ctaSection}>
        <HeadingBox
          title="Ready to Meet Our Team?"
          description="Schedule a consultation with our medical experts and discover how we can help you achieve your aesthetic goals"
          textColor="var(--color-secondary)"
        />
        <Box className={styles.ctaImagesGrid}>
          <Box className={styles.ctaImageWrapper}>
            <img
              src={hairCareImage}
              alt="Hair Care"
              className={styles.ctaImage}
            />
          </Box>
          <Box className={styles.ctaImageWrapper}>
            <img
              src={eyeCareImage}
              alt="Eye Care"
              className={styles.ctaImage}
            />
          </Box>
          <Box className={styles.ctaImageWrapper}>
            <img
              src={laserImage}
              alt="Laser Treatment"
              className={styles.ctaImage}
            />
          </Box>
          <Box className={styles.ctaImageWrapper}>
            <img
              src={physiotherapyImage}
              alt="Physiotherapy"
              className={styles.ctaImage}
            />
          </Box>
        </Box>
        <Flex
          direction="column"
          align="center"
          gap="4"
          className={styles.ctaContent}
        >
          <Text className={styles.ctaDescription}>
            Our team of medical professionals is here to guide you through your
            aesthetic journey with expertise, care, and compassion.
          </Text>
          <Flex gap="4" wrap="wrap" justify="center">
            <Button
              size="4"
              variant="normal"
              backgroundColor="var(--color-secondary)"
              textColor="var(--color-primary)"
              onClick={() => {
                window.location.href = "/contact";
              }}
              className={styles.ctaButtonBook}
            >
              Book a Consultation
            </Button>
            <Button
              size="4"
              variant="outlined"
              backgroundColor="var(--color-secondary)"
              textColor="var(--color-primary)"
              onClick={() => {
                // Navigate to services page
                window.location.href = "/services";
              }}
              className={styles.ctaButton}
            >
              Explore Our Services
            </Button>
          </Flex>
        </Flex>
      </Box>
    </div>
  );
};

export default Team;
